import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import { and } from 'tio-ui/utilities';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import Drawer from 'tio-ui/components/drawer';
import Button from 'tio-ui/components/button';
import { PhBank, PhCheckCircle, PhRadioButton, PhArrowsLeftRight } from 'ember-phosphor-icons';
import { query } from '@warp-drive/utilities/json-api';
import type { AccountExtension as AccountModel } from 'tio-data/data/resources/account.ext';
import type { EmployeePlanAccountResource } from 'tio-data/data/resources/employee-plan-account.type';
import { updateContributionOrderRequest } from 'tio-data/requests/employee-plan-account';
import { updateContributionAccountRequest } from 'tio-data/requests/person';
import type NotificationsService from 'tio-ui/services/notifications';
import type SlrLoanStandingVerificationService from 'tio-employee/services/slr-loan-standing-verification';
import type { Store as StoreService } from '@warp-drive/core';

/** Selects the linked loan account receiving employer contributions and notifies the caller after saving. */
interface AccountSelectorDrawerSignature {
  Args: {
    /** Whether the drawer is open */
    isOpen: boolean;
    /** Called when the drawer should close */
    onClose: () => void;
    /** All linked accounts the user can choose from */
    accounts: AccountModel[];
    /** The account currently receiving contributions */
    currentAccount: AccountModel | null;
    /** Person ID for the API call */
    personId: string | null;
    /** Called after the account is successfully changed */
    onAccountChanged?: () => void;
  };
  Element: HTMLDivElement;
}

export default class AccountSelectorDrawer extends Component<AccountSelectorDrawerSignature> {
  @service declare store: StoreService;
  @service declare notifications: NotificationsService;
  @service declare slrLoanStandingVerification: SlrLoanStandingVerificationService;

  @tracked private _selectedAccountId: string | null = null;
  @tracked private hasUserSelectedAccount = false;

  get selectedAccountId() {
    if (this.hasUserSelectedAccount) {
      return this._selectedAccountId;
    }

    return this.args.currentAccount?.id ?? null;
  }

  set selectedAccountId(value: string | null) {
    this.hasUserSelectedAccount = true;
    this._selectedAccountId = value;
  }

  get isNewAccountSelected() {
    return this.selectedAccountId !== (this.args.currentAccount?.id ?? null);
  }

  get isSaveDisabled() {
    return !this.selectedAccountId || !this.isNewAccountSelected;
  }

  selectAccount = (accountId: string | null) => {
    this.selectedAccountId = accountId;
  };

  resetSelection = () => {
    this.hasUserSelectedAccount = false;
    this._selectedAccountId = null;
  };

  handleClose = () => {
    this.resetSelection();
    this.args.onClose();
  };

  updateAccountTask = dropTask(async () => {
    if (!this.selectedAccountId || !this.args.personId) return;

    try {
      const orderedAccountIds = [
        this.selectedAccountId,
        ...this.args.accounts
          .filter((account) => account.id !== this.selectedAccountId)
          .map((account) => account.id),
      ].filter((id): id is string => typeof id === 'string');

      const { content } = (await this.store.request(
        query<EmployeePlanAccountResource>('employee-plan-account', {
          'filter[account_id]': this.selectedAccountId,
        })
      )) as { content: { data: EmployeePlanAccountResource[] } };

      // Dual-written alongside the new per-plan contribution_order until the old
      // person-wide public_ordinal_value path is deliberately sunset.
      await Promise.all([
        this.store.request(updateContributionAccountRequest(this.args.personId, orderedAccountIds)),
        ...content.data
          .filter((epa): epa is EmployeePlanAccountResource & { id: string } => !!epa.id)
          .map((epa) => this.store.request(updateContributionOrderRequest(epa.id, 1))),
      ]);

      // Retire the service-scoped upload guidance here so receiving-account changes from every route clear it.
      this.slrLoanStandingVerification.handleReceivingAccountChanged();
      this.notifications.success('Contribution account updated successfully.');
      this.args.onAccountChanged?.();
      this.handleClose();
    } catch (error) {
      console.error('Error updating contribution account:', error);
      this.notifications.error('Failed to update contribution account. Please try again.');
    }
  });

  <template>
    {{! template-lint-disable no-bare-strings }}
    <Drawer
      data-test-account-selector-drawer
      @isOpen={{@isOpen}}
      @onClose={{this.handleClose}}
      @size="lg"
      @placement="right"
      ...attributes
    >
      <:header>
        <div class="tio-row">
          <div class="p-2 tio-bg-brand-soft rounded-lg">
            <PhArrowsLeftRight @size={{20}} class="tio-fg-brand" />
          </div>
          <div>
            <Heading @level={{3}}>
              Change Contribution Account
            </Heading>
            <Text @size="sm" class="tio-fg-muted">
              Select which account receives employer contributions
            </Text>
          </div>
        </div>
      </:header>
      <:body>
        <div class="space-y-3">
          {{#if @currentAccount}}
            <div class="tio-row-tight mb-4 pb-4 border-b tio-border-default">
              <PhBank @size={{16}} class="tio-fg-disabled" />
              <Text @size="sm" class="tio-fg-muted">
                Currently receiving:
                <span class="font-medium tio-fg-primary">{{@currentAccount.institutionName}}</span>
              </Text>
            </div>
          {{/if}}

          {{#each @accounts as |account|}}
            <button
              type="button"
              data-test-account-selector-option={{account.id}}
              class="w-full tio-row-loose p-4 rounded-xl border-2 transition-all text-left
                {{if
                  (eq this.selectedAccountId account.id)
                  'tio-border-brand tio-bg-brand-soft shadow-sm'
                  'tio-border-default hover:tio-border-brand hover:tio-bg-sunken'
                }}"
              {{on "click" (fn this.selectAccount account.id)}}
            >
              <div class="shrink-0">
                {{#if (eq this.selectedAccountId account.id)}}
                  <PhCheckCircle @size={{24}} class="tio-fg-brand" />
                {{else}}
                  <PhRadioButton @size={{24}} class="tio-fg-muted" />
                {{/if}}
              </div>
              <div class="tio-fill-min">
                {{! `truncate` cannot work here: Text renders an inline <span>, and a non-replaced inline element ignores overflow/text-overflow while still honouring white-space:nowrap — so a long servicer name ("EdFinancial Services - Federal Direct Loan Program (FDLP)") could neither wrap nor clip, and escaped the row. Wrap instead, breaking inside a word only if one token is wider than the row. }}
                <Text @weight="medium" class="break-words">
                  {{account.institutionName}}
                </Text>
                {{#if account.number}}
                  <Text @size="sm" class="tio-fg-muted">
                    Account ····{{lastFour account.number}}
                  </Text>
                {{/if}}
              </div>
              {{#if (and @currentAccount (eq @currentAccount.id account.id))}}
                <span
                  class="shrink-0 tio-text-xs-medium px-2 py-0.5 rounded-full tio-bg-success-soft tio-fg-success-body"
                >
                  Current
                </span>
              {{/if}}
            </button>
          {{else}}
            <div class="text-center py-8">
              <PhBank @size={{48}} class="mx-auto tio-fg-muted mb-3" />
              <Text class="tio-fg-muted">No linked accounts found.</Text>
              <Text @size="sm" class="tio-fg-disabled">
                Link a loan account to receive employer contributions.
              </Text>
            </div>
          {{/each}}
        </div>
      </:body>
      <:footer>
        <div class="tio-flex-end gap-3">
          <Button
            @appearance="outlined"
            data-test-account-selector-cancel-button
            {{on "click" this.handleClose}}
          >
            Cancel
          </Button>
          <Button
            @intent="primary"
            data-test-account-selector-update-button
            @isRunning={{this.updateAccountTask.isRunning}}
            @isDisabled={{this.isSaveDisabled}}
            {{on "click" this.updateAccountTask.perform}}
          >
            Update Account
          </Button>
        </div>
      </:footer>
    </Drawer>
  </template>

  // Pure helpers used in template
}

function lastFour(value: string): string {
  return value.slice(-4);
}
